import { ChangeEvent, CSSProperties, FormEvent, useEffect, useState } from "react";
import { useIonRouter } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "pages";
import { useLocation } from "react-router-dom";
import { Alert, Colors, Icon } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { Loader } from "~components/Atoms";
import { fetchSubMapSubs, getFeatureFlags, translate, useURLSearchParams } from "~utils";

import { SubMapCard } from "./components";
import { PossibleSlugs, searchableTerms } from "./searchableTerms";
import styles from "./SubMapTool.module.css";

export function SubMapTool() {
  const queryParam = useURLSearchParams();

  const values = {};
  searchableTerms.forEach((item) => {
    const key = item.slug;
    const value = queryParam.get(key);
    if (!value) return;
    Object.assign(values, { [key]: value });
  });

  const {
    error,
    isFetching,
    data: subListData,
  } = useQuery({
    queryKey: ["fetchSubMapSubList", values],
    queryFn: async () => {
      const { data } = await fetchSubMapSubs(values);
      return data;
    },
    retry: false,
    enabled: Object.keys(values).length > 0,
    gcTime: 0, // immediately garbage collect initial "params === null" query
    refetchInterval: 5 * 60 * 1000, // time in ms - minutes * seconds * milliseconds = 5 minutes
    refetchOnWindowFocus: false, // Refetches can be very expensive, and data persistence is more important here than being extremely up-to-date
  });

  return (
    <div className="page">
      {isFetching ? (
        <div className={styles["loading-interstitial-score"]}>
          <Loader />
        </div>
      ) : (
        <SubMapToolView error={error} subsData={subListData} />
      )}
    </div>
  );
}

export type SubData = {
  name: string;
  id: string;
  profileImageUrl: string;
  about?: string;
  stats?: {
    positiveReviews?: number;
    daysWorked?: number;
    schoolsWorked?: number;
    timesPreferred?: number;
  };
  isSwingHero?: boolean;
  specialQualifications: {
    isStarSub: boolean;
    isHighlyReliable: boolean;
    isAlwaysOnTime: boolean;
    isLookingForNewOpportunities: boolean;
    enjoysLongTermRequests: boolean;
  };
};

type SubMapToolViewProps = {
  error: Error | null;
  subsData: SubData[];
};

type ErrorType = Error & {
  response: {
    data: {
      message: string;
    };
  };
};

export function SubMapToolView(props: SubMapToolViewProps) {
  const { error, subsData } = props;
  const { userInfo } = useAuth();
  const { SUB_MAP_TOOL } = getFeatureFlags(userInfo);
  const router = useIonRouter();

  if (!SUB_MAP_TOOL) {
    router.push("/");
  }

  return (
    <div>
      <SubMapSearch />
      {error ? (
        <div className={styles["location-settings-message-wrapper"]}>
          <AlertError margin="0px" errorMsg={(error as ErrorType)?.response?.data?.message} />
        </div>
      ) : (
        <div className={styles.subsGrid}>
          {subsData?.length > 0 ? (
            subsData.map((sub) => {
              return <SubMapCard sub={sub} key={sub.id} />;
            })
          ) : (
            <div>{translate("SUB_MAP_SUBS_NOT_FOUND")}</div>
          )}
        </div>
      )}
      {subsData?.length > 0 && (
        <p className="italic w-full text-center">{translate("LABEL_END_OF_PAGE")}!</p>
      )}
    </div>
  );
}

const removeSearchTerm = (urlParam: URLSearchParams, term: PossibleSlugs) => {
  urlParam.delete(term);
  return `?${urlParam.toString()}`;
};

type SubMapGenericInputProps = {
  activeSearchType: string;
};

const SubMapGenericInput: React.FC<SubMapGenericInputProps> = ({ activeSearchType }) => {
  const urlParam = useURLSearchParams();
  const router = useIonRouter();
  const searchTerm = urlParam.get(activeSearchType);
  const [_searchTerm, _setSearchTerm] = useState<string>(searchTerm || "");
  const location = useLocation();
  const currentSearchItem = searchableTerms.find((item) => item.slug === activeSearchType);

  const icon = currentSearchItem?.icon || "Person";

  useEffect(() => {
    // Set input on direct URL manipulation
    _setSearchTerm(searchTerm || "");
  }, [location, searchTerm]);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    _setSearchTerm(val);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    return router.push(
      location.pathname + handleUpdateUrl(urlParam, { [activeSearchType]: _searchTerm }),
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles["input-wrapper"]}>
        <Icon name={icon} aria-hidden="true" />
        <input
          data-testid="location-search-input"
          className={styles["custom-input"]}
          type="text"
          placeholder={currentSearchItem?.placeholder}
          value={_searchTerm}
          onChange={handleInput}
          required
        />
        <button type="submit" className={styles["input-submit-btn"]}>
          <Icon name="Arrow Right" color={Colors.blue500} />
        </button>
      </div>
    </form>
  );
};

function SubMapSearch() {
  const [searchType, setSearchType] = useState<PossibleSlugs>("zip");
  const urlParam = useURLSearchParams();
  const router = useIonRouter();
  const location = useLocation();
  const hasActiveSearchTerms = urlParam.size > 0;

  const onRemoveSearchTerm = (term: PossibleSlugs) => {
    router.push(location.pathname + removeSearchTerm(urlParam, term));
  };

  return (
    <div className={`${styles["location-search"]}`}>
      <p>{translate("SUB_MAP_SEARCHING_BY")}</p>
      <select
        value={searchType}
        className={styles.selectDropdown}
        onChange={(v) => setSearchType(v.target.value as PossibleSlugs)}
      >
        {searchableTerms.map((item) => {
          return (
            <option value={item.slug} key={item.slug}>
              {item.labelName}
            </option>
          );
        })}
      </select>

      <SubMapGenericInput activeSearchType={searchType} />

      {hasActiveSearchTerms && (
        <p className={styles["location-search-search-term"]}>{translate("SUB_MAP_MATCH_ALL")}</p>
      )}
      <ul>
        {searchableTerms.map((item, idx) => {
          const itemVal = urlParam.get(item.slug);
          if (!itemVal) return;
          return (
            <li
              key={idx}
              className="flex gap-2 mb-2 cursor-pointer"
              onClick={() => {
                onRemoveSearchTerm(item.slug);
              }}
            >
              {item.searchString(itemVal)}
              <Icon name="Delete" color={Colors.red500} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type submapURLUpdateObject = {
  zip?: string;
  school?: string;
  "sub-name"?: string;
};

const handleUpdateUrl = (urlParamObject: URLSearchParams, updateObj: submapURLUpdateObject) => {
  for (const key in updateObj) {
    urlParamObject.set(key, updateObj[key as keyof typeof updateObj] || "");
  }
  return `?${urlParamObject.toString()}`;
};

type AlertErrorProps = {
  margin?: CSSProperties["margin"];
  errorMsg?: string;
};

function AlertError(props: AlertErrorProps) {
  const { margin, errorMsg = translate("LABEL_SOMETHING_WENT_WRONG") } = props;
  return (
    <div css={{ margin: margin ? margin : "16px 0px" }}>
      <Alert color="danger" showIcon title={errorMsg}>
        <span>
          {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
          <a
            target="_blank"
            rel="noreferrer"
            href={generateSwingSupportURL(translate("SWING_SUPPORT_LINK_GENERAL_REQUEST"))}
          >
            {translate("LABEL_CONTACT_SUPPORT")}
          </a>{" "}
          {translate("LABEL_CONTACT_SUPPORT_2")}
        </span>
      </Alert>
    </div>
  );
}
